import { useState, useEffect, useRef } from 'react';
import { toast } from 'sonner';
import api from '../../utils/api';
import { Send, Bot, User, MessageSquare, ExternalLink, Copy, Loader2, Phone, Check } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import BackButton from '../../components/BackButton';
import useAuthStore from '../../store/authStore';

const WHATSAPP_NUMBER = '255753541330';  // Tanzania: +255, without leading 0
const WHATSAPP_DISPLAY = '+255 753 541 330';

const AIAssistant = () => {
  const { user } = useAuthStore();
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(true);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    loadChatHistory();
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const loadChatHistory = async () => {
    try {
      const response = await api.get('/ai/chat/history');
      // Convert history to message format (reverse to show oldest first)
      const historyMessages = [];
      response.data.reverse().forEach(item => {
        historyMessages.push({
          id: item.id + '-user',
          type: 'user',
          content: item.message,
          timestamp: item.created_at
        });
        historyMessages.push({
          id: item.id + '-ai',
          type: 'ai',
          content: item.response,
          reference_id: item.reference_id,
          timestamp: item.created_at
        });
      });
      setMessages(historyMessages);
    } catch (error) {
      console.error('Failed to load chat history:', error);
      if (error.response?.status === 403) {
        toast.error('Access restricted. Only @ahsga.me members can use AI Assistant.');
      }
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!inputMessage.trim() || loading) return;

    const userMessage = {
      id: Date.now() + '-user',
      type: 'user',
      content: inputMessage,
      timestamp: new Date().toISOString()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setLoading(true);

    try {
      const response = await api.post('/ai/chat', { message: inputMessage });
      
      const aiMessage = {
        id: Date.now() + '-ai',
        type: 'ai',
        content: response.data.response,
        reference_id: response.data.reference_id,
        timestamp: response.data.timestamp
      };
      
      setMessages(prev => [...prev, aiMessage]);
    } catch (error) {
      console.error('Chat error:', error);
      toast.error(error.response?.data?.detail || 'Failed to get response');
      // Remove the user message if AI failed
      setMessages(prev => prev.filter(m => m.id !== userMessage.id));
    } finally {
      setLoading(false);
    }
  };

  const generateWhatsAppUrl = (aiResponse, referenceId) => {
    const message = `AHSGA App Support Request
Name: ${user?.full_name || 'Member'}
Role: ${user?.role || 'member'}
Email: ${user?.email || ''}
Message: ${aiResponse.substring(0, 500)}${aiResponse.length > 500 ? '...' : ''}
Ref: ${referenceId}`;
    
    return `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(message)}`;
  };

  const handleSendToWhatsApp = async (aiResponse, referenceId) => {
    try {
      // Log the action
      await api.post('/ai/whatsapp-log', {
        message_preview: aiResponse.substring(0, 200),
        reference_id: referenceId,
        destination: 'support'
      });
      
      // Create a temporary link and click it - works better than window.open
      const url = generateWhatsAppUrl(aiResponse, referenceId);
      const link = document.createElement('a');
      link.href = url;
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      toast.success(`Reference ID: ${referenceId}`, {
        description: 'Opening WhatsApp...'
      });
    } catch (error) {
      console.error('WhatsApp error:', error);
      // Still try to open WhatsApp even if logging fails
      const url = generateWhatsAppUrl(aiResponse, referenceId);
      const link = document.createElement('a');
      link.href = url;
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const copyPhoneNumber = () => {
    navigator.clipboard.writeText(WHATSAPP_DISPLAY);
    toast.success('Phone number copied: ' + WHATSAPP_DISPLAY);
  };

  const copyReferenceId = (referenceId) => {
    navigator.clipboard.writeText(referenceId);
    toast.success('Reference ID copied!');
  };

  const formatTime = (timestamp) => {
    return new Date(timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  if (historyLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="h-[calc(100vh-120px)] flex flex-col">
      <BackButton to="/member" />
      
      <div className="flex items-center space-x-3 mb-4">
        <div className="bg-gradient-to-br from-blue-500 to-purple-600 p-3 rounded-xl">
          <Bot className="w-6 h-6 text-white" />
        </div>
        <div>
          <h1 className="text-2xl font-serif font-bold text-slate-900">AI Assistant</h1>
          <p className="text-sm text-slate-500">Ask questions about AHSGA Portal</p>
        </div>
      </div>

      {/* Chat Messages */}
      <div className="flex-1 overflow-y-auto bg-slate-50 rounded-xl p-4 mb-4 space-y-4">
        {messages.length === 0 && (
          <div className="text-center py-12">
            <Bot className="w-16 h-16 mx-auto mb-4 text-slate-300" />
            <p className="text-slate-500 mb-2">Welcome, {user?.full_name}!</p>
            <p className="text-sm text-slate-400">
              Ask me anything about the AHSGA Portal. I'm here to help!
            </p>
          </div>
        )}

        {messages.map((message) => (
          <div
            key={message.id}
            className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}
          >
            <div
              className={`max-w-[80%] rounded-2xl px-4 py-3 ${
                message.type === 'user'
                  ? 'bg-slate-900 text-white rounded-br-md'
                  : 'bg-white text-slate-800 shadow-sm border border-slate-200 rounded-bl-md'
              }`}
            >
              <div className="flex items-start space-x-2">
                {message.type === 'ai' && (
                  <Bot className="w-5 h-5 text-blue-500 mt-0.5 flex-shrink-0" />
                )}
                <div className="flex-1">
                  <p className="whitespace-pre-wrap text-sm">{message.content}</p>
                  
                  {message.type === 'ai' && message.reference_id && (
                    <div className="mt-3 pt-3 border-t border-slate-200 space-y-2">
                      <div className="flex items-center space-x-2 text-xs text-slate-500">
                        <span>Ref: {message.reference_id}</span>
                        <button
                          onClick={() => copyReferenceId(message.reference_id)}
                          className="hover:text-slate-700"
                          title="Copy Reference ID"
                        >
                          <Copy className="w-3 h-3" />
                        </button>
                      </div>
                      
                      {/* WhatsApp Contact Info */}
                      <div className="bg-green-50 rounded-lg p-2 text-xs">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-green-700 font-medium">Contact Support:</span>
                          <button
                            onClick={copyPhoneNumber}
                            className="flex items-center space-x-1 text-green-600 hover:text-green-800"
                          >
                            <Phone className="w-3 h-3" />
                            <span className="font-mono">{WHATSAPP_DISPLAY}</span>
                            <Copy className="w-3 h-3" />
                          </button>
                        </div>
                        <Button
                          size="sm"
                          variant="outline"
                          className="w-full text-xs bg-green-100 border-green-300 text-green-700 hover:bg-green-200"
                          onClick={() => handleSendToWhatsApp(message.content, message.reference_id)}
                        >
                          <MessageSquare className="w-3 h-3 mr-1" />
                          Send to WhatsApp
                          <ExternalLink className="w-3 h-3 ml-1" />
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
                {message.type === 'user' && (
                  <User className="w-5 h-5 text-slate-300 mt-0.5 flex-shrink-0" />
                )}
              </div>
              <p className={`text-xs mt-1 ${message.type === 'user' ? 'text-slate-400' : 'text-slate-400'}`}>
                {formatTime(message.timestamp)}
              </p>
            </div>
          </div>
        ))}

        {loading && (
          <div className="flex justify-start">
            <div className="bg-white text-slate-800 shadow-sm border border-slate-200 rounded-2xl rounded-bl-md px-4 py-3">
              <div className="flex items-center space-x-2">
                <Bot className="w-5 h-5 text-blue-500" />
                <div className="flex space-x-1">
                  <div className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                  <div className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                  <div className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                </div>
              </div>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Input Form */}
      <form onSubmit={handleSendMessage} className="flex space-x-2">
        <Input
          value={inputMessage}
          onChange={(e) => setInputMessage(e.target.value)}
          placeholder="Type your message..."
          disabled={loading}
          className="flex-1"
          data-testid="chat-input"
        />
        <Button
          type="submit"
          disabled={loading || !inputMessage.trim()}
          className="bg-slate-900 hover:bg-slate-800"
          data-testid="chat-send-btn"
        >
          {loading ? (
            <Loader2 className="w-5 h-5 animate-spin" />
          ) : (
            <Send className="w-5 h-5" />
          )}
        </Button>
      </form>
    </div>
  );
};

export default AIAssistant;
